import { ActivatedRouteSnapshot, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { DocumentService } from '@literax/services/document/document.service';
import { EWorkingMode } from '@literax/modules/documents/store/workspace/workspace.state';
import Hashids from 'hashids';
import { IAppState } from '@literax/store';
import { IDocumentResponse } from '@literax/models/document.model';
import { ILinkOutcomeResolution } from '@literax/models/link-outcome.model';
import { Injectable } from '@angular/core';
import { LinkOutcomeService } from '@literax/services/link-outcome/link-outcome.service';
import { Store } from '@ngrx/store';
import { ValidateDocumentSignatureSuccess } from '@literax/store/free-document/free-document.actions';
import { WorkspaceActions } from '@literax/modules/documents/store/workspace/workspace.actions';
import jwt_decode from 'jwt-decode';

/**
 * Entry guard for every guest link that opens a document without a platform session.
 *
 * It resolves to exactly one of two things: `true`, when the document can be opened, or a
 * `UrlTree` pointing at the screen that explains why it cannot (FR-004). It never throws, never
 * emits the error onward, and never resolves to `false` — those were the three ways a recipient
 * ended up looking at a blank page.
 *
 * Why the outcome is not decided here: every reason a link cannot be opened maps to a screen in
 * exactly one place, {@link LinkOutcomeService}. This guard's job is to hand it the failure and
 * navigate where it says (D-03).
 */
@Injectable({
  providedIn: 'root',
})
export class ValidateTokenDocumentFreeGuard  {
  constructor(
    private store: Store<IAppState>,
    private docService: DocumentService,
    private linkOutcome: LinkOutcomeService
  ) {}

  canActivate(
    next: ActivatedRouteSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const token = next.paramMap.get('token');

    return this.isValid(token);
  }

  /**
   * Decides whether the link can be opened, and where to send the recipient if it cannot.
   *
   * @param token The guest token taken from the route.
   * @returns `true` to activate, or a `UrlTree` for the screen that explains the outcome.
   */
  isValid(token: string): Observable<boolean | UrlTree> | boolean | UrlTree {
    this.store.dispatch(WorkspaceActions.setGuestToken({ payload: token }));

    const tokenDecoded = this.decode(token);

    // A token that cannot be read is decided here, before any request goes out: there is nothing
    // to ask the backend about, and a cut link must never cost the recipient a round trip only to
    // land on a generic error (FR-027).
    if (tokenDecoded === null) {
      return this.destinationFor(this.linkOutcome.resolveTruncated());
    }

    const documentId = this.documentIdFrom(tokenDecoded);

    return this.docService.getDocumentById(documentId, token).pipe(
      map((document: IDocumentResponse) => {
        this.store.dispatch(
          WorkspaceActions.setWorkspaceMode({
            payload: { mode: EWorkingMode.GUEST },
          })
        );

        this.store.dispatch(
          WorkspaceActions.getUserInfo({
            payload: { email: tokenDecoded['email'], token: token },
          })
        );

        this.store.dispatch(
          WorkspaceActions.getAccessTokens({
            payload: {
              body: {
                textEditor: true,
                chat: true,
              },
              token: token,
            },
          })
        );

        this.store.dispatch(
          ValidateDocumentSignatureSuccess({
            payload: { document, token },
          })
        );

        this.store.dispatch(
          WorkspaceActions.getDocumentSuccess({
            payload: { document, getAttachment: true, token },
          })
        );

        return true;
      }),
      catchError((error) => {
        // Resolved once, before the dispatch rather than after it: the outcome the completed
        // screen reads is the same object that chooses the destination, so the wording and the
        // route can never disagree. The `UrlTree` below is built from this same resolution and
        // is therefore byte-for-byte what it was (FR-030, FR-023, D-13).
        const resolution = this.linkOutcome.resolve(error);

        this.store.dispatch(
          WorkspaceActions.getDocumentError({
            payload: { error, outcome: resolution.outcome },
          })
        );

        return of(this.destinationFor(resolution));
      })
    );
  }

  /**
   * Reads the token's claims, without deciding anything from them.
   *
   * Expiry in particular is never judged here — that verdict belongs to the backend, which knows
   * that an observer's link does not expire (FR-026, FR-024).
   *
   * @param token The guest token taken from the route.
   * @returns The decoded claims, or `null` when the token cannot be read at all.
   */
  private decode(token: string): Record<string, string> | null {
    try {
      return jwt_decode(token);
    } catch {
      return null;
    }
  }

  /**
   * @param tokenDecoded The token's claims.
   * @returns The document id the claims point at.
   */
  private documentIdFrom(tokenDecoded: Record<string, string>): number {
    const hashedDuid = tokenDecoded['uuid'];

    if (!isNaN(Number(hashedDuid))) {
      return hashedDuid as unknown as number;
    }

    // Built only on the branch that needs it, rather than on every activation.
    const hashids = new Hashids(
      'ce3da9a74158e0ab11288fef3f8b0ffbafd18127ab2865b9889c6196c5f57e938f964b65fa5fafe2701c0bc555dbef3706a3e063d4dc24fd3b5a814f6783183b',
      15
    );

    return hashids.decode(hashedDuid)?.pop() as number;
  }

  /**
   * Turns a resolved outcome into something the router accepts.
   *
   * @param resolution The outcome and its destination.
   * @returns The `UrlTree` to redirect to, or `true` when the outcome needs no redirection.
   */
  private destinationFor(resolution: ILinkOutcomeResolution): boolean | UrlTree {
    return resolution.urlTree ?? true;
  }
}
